import posthog from "posthog-js";

import AuthService from "@/services/auth";
import Client from "@/services/client";

const client = new Client();

/**
 * Última coorte sincronizada nesta sessão (`worldId:valor`). Evita repetir o
 * POST a cada navegação — o valor só muda quando a flag muda.
 */
let lastSyncedKey: string | null = null;

export function clearNewSystemSyncCache(): void {
  lastSyncedKey = null;
}

/**
 * Propaga a coorte do "novo sistema" avaliada no Manager para o resto do produto.
 *
 * Contexto: a allowlist da coorte vive em UM lugar — a flag PostHog
 * `manager-new-ui-ab`. Antes, as 5 flags do app (`nova-navbar`, `trilhas`,
 * `check-in-diario`, `mural-escola`, `agenda-e-news`) repetiam a mesma allowlist
 * à mão, e sair de sincronia era silencioso: marcava-se a unidade no Manager e o
 * app simplesmente não mudava. Agora o Manager resolve a flag e propaga o
 * RESULTADO para dois destinos:
 *
 * 1. `account.newSystem` no Mongo — fonte primária lida pelo app novo via
 *    GET /worlds/:worldId/account-info (sem PostHog no boot do app).
 * 2. prop de group `new_system` — só para os builds ANTIGOS do app, que ainda
 *    leem as 5 flags e continuam em produção (AGENTS.md §12).
 *
 * Ver readme/POSTHOG_MANAGER_APP_CONTEXT.md.
 *
 * @param worldId world da conta logada (group key do PostHog)
 * @param cohortNewUi coorte REAL — use `useNewUi().cohortNewUi`, nunca `isNewUi`,
 *   que embute o override local de QA da conta teste.
 */
export async function syncNewSystemCohort(worldId: string | null | undefined, cohortNewUi: boolean): Promise<void> {
  if (typeof window === "undefined" || !AuthService.isLogged()) return;

  const key = `${worldId || "authenticated-session"}:${cohortNewUi}`;
  if (lastSyncedKey === key) return;
  // Marca antes do await: evita POST duplicado se o efeito reentrar.
  lastSyncedKey = key;

  if (worldId) {
    posthog.group("account", worldId, { new_system: cohortNewUi });
  }

  // authenticatedRequest RESOLVE {error} em vez de rejeitar — checar explicitamente,
  // senão a falha passa calada e a coorte fica parada no banco sem ninguém saber.
  const result = await client.authenticatedRequest("POST", "users/account/new-system", {
    newSystem: cohortNewUi,
  });

  if (result?.error) {
    // Libera o retry na próxima navegação/login.
    lastSyncedKey = null;
    console.error(
      "[new-system-sync] falha ao persistir a coorte do novo sistema",
      { worldId, newSystem: cohortNewUi, error: result.error }
    );
  }
}
